import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { apiService, getApiErrorMessage } from '../services/apiService';

interface AjanlatData {
  datum: Date;
  ajanlatTargya: string;
  ugyintezoId: string;
  valasz: string;
  valaszDatum: Date | null;
  megrendeloId: number;
}

interface Megrendelo {
  id: number;
  cegnev: string;
}

interface User {
  id: string;
  userName: string;
}

interface AddAjanlatDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddAjanlatDialog: React.FC<AddAjanlatDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<AjanlatData>({
    datum: new Date(),
    ajanlatTargya: '',
    ugyintezoId: '',
    valasz: '',
    valaszDatum: null,
    megrendeloId: 0,
  });

  const [megrendelok, setMegrendelok] = useState<Megrendelo[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedMegrendelo, setSelectedMegrendelo] = useState<Megrendelo | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autocomplete states
  const [megrendeloOptions, setMegrendeloOptions] = useState<Megrendelo[]>([]);
  const [megrendeloLoading, setMegrendeloLoading] = useState(false);
  const [megrendeloInputValue, setMegrendeloInputValue] = useState('');
  
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');

  // Adatok betöltése dialógus megnyitásakor
  useEffect(() => {
    if (open) {
      // Reset autocomplete options when dialog opens
      setMegrendeloOptions([]);
      setUserOptions([]);
    }
  }, [open]);

  // Megrendelő keresés
  const searchMegrendelok = async (query: string) => {
    if (!query || query.length < 2) {
      setMegrendeloOptions([]);
      return;
    }

    setMegrendeloLoading(true);
    try {
      const response = await apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`);
      setMegrendeloOptions(response.data || []);
    } catch (error) {
      console.error('Error searching megrendelok:', error);
      setMegrendeloOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült a megrendelők keresése.'));
    } finally {
      setMegrendeloLoading(false);
    }
  };

  // User keresés (ügyintéző)
  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserOptions([]);
      return;
    }

    setUserLoading(true);
    try {
      const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
      setUserOptions(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült az ügyintézők keresése.'));
    } finally {
      setUserLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validáció
    if (!selectedMegrendelo) {
      setError('Kérjük, válasszon megrendelőt!');
      return;
    }
    if (!selectedUser) {
      setError('Kérjük, válasszon ügyintézőt!');
      return;
    }
    if (!formData.ajanlatTargya.trim()) {
      setError('Kérjük, adja meg az ajánlat tárgyát!');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        Datum: formData.datum,
        AjanlatTargya: formData.ajanlatTargya,
        UgyintezoId: selectedUser.id,
        Valasz: formData.valasz || null,
        ValaszDatum: formData.valaszDatum,
        MegrendeloId: selectedMegrendelo.id,
      };

      await apiService.createData('ajanlatok/create', submitData);
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating ajanlat:', err);
      setError(getApiErrorMessage(err, 'Hiba történt az ajánlat létrehozása során.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      datum: new Date(),
      ajanlatTargya: '',
      ugyintezoId: '',
      valasz: '',
      valaszDatum: null,
      megrendeloId: 0,
    });
    setSelectedMegrendelo(null);
    setSelectedUser(null);
    setError(null);
    setMegrendeloOptions([]);
    setUserOptions([]);
    setMegrendeloInputValue('');
    setUserInputValue('');
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Dialog 
        open={open} 
        onClose={handleCancel} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px' }
        }}
      >
        <DialogTitle>Új Ajánlat Hozzáadása</DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Dátum */}
              <DatePicker
                label="Dátum *"
                value={formData.datum}
                onChange={(newValue) => {
                  if (newValue) {
                    setFormData(prev => ({ ...prev, datum: newValue }));
                  }
                }}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />

              {/* Megrendelő */}
              <Autocomplete
                options={megrendeloOptions}
                getOptionLabel={(option) => option.cegnev}
                value={selectedMegrendelo}
                onChange={(_, newValue) => setSelectedMegrendelo(newValue)}
                inputValue={megrendeloInputValue}
                onInputChange={(_, newInputValue) => {
                  setMegrendeloInputValue(newInputValue);
                  searchMegrendelok(newInputValue);
                }}
                loading={megrendeloLoading}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Megrendelő *" 
                    fullWidth
                    error={!!error && !selectedMegrendelo}
                    helperText={!!error && !selectedMegrendelo ? 'Megrendelő kiválasztása kötelező' : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {megrendeloLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="Nincs találat - írjon be legalább 2 karaktert"
              />

              {/* Ajánlat tárgya */}
              <TextField
                label="Ajánlat tárgya *"
                fullWidth
                multiline
                rows={3}
                value={formData.ajanlatTargya}
                onChange={(e) => setFormData(prev => ({ ...prev, ajanlatTargya: e.target.value }))}
                error={!!error && !formData.ajanlatTargya.trim()}
                helperText={!!error && !formData.ajanlatTargya.trim() ? 'Ajánlat tárgya megadása kötelező' : ''}
              />

              {/* Ügyintéző és Válasz */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option) => option.userName}
                  value={selectedUser}
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  inputValue={userInputValue}
                  onInputChange={(_, newInputValue) => {
                    setUserInputValue(newInputValue);
                    searchUsers(newInputValue);
                  }}
                  loading={userLoading}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Ügyintéző *" 
                      fullWidth
                      error={!!error && !selectedUser}
                      helperText={!!error && !selectedUser ? 'Ügyintéző kiválasztása kötelező' : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText="Nincs találat - írjon be legalább 2 karaktert"
                  sx={{ flex: 1 }}
                />

                <FormControl fullWidth sx={{ flex: 1 }}>
                  <InputLabel>Válasz</InputLabel>
                  <Select
                    value={formData.valasz}
                    label="Válasz"
                    onChange={(e) => setFormData(prev => ({ ...prev, valasz: e.target.value }))}
                  >
                    <MenuItem value="">Nincs megadva</MenuItem>
                    <MenuItem value="Elfogadva">Elfogadva</MenuItem>
                    <MenuItem value="Elutasítva">Elutasítva</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Válasz dátuma */}
              <DatePicker
                label="Válasz dátuma"
                value={formData.valaszDatum}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, valaszDatum: newValue }));
                }}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Mégse
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Létrehozás...' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddAjanlatDialog;
